// ===============================================================
// ProBank — Virtual Card Display
//
// Realistic 3D card with flip animation and three tier designs:
//   classic  — deep navy + silver
//   gold     — black + metallic gold
//   platinum — brushed silver/white
//
// Props:
//   tier        — "classic" | "gold" | "platinum"
//   cardNumber  — shown masked unless `revealed`
//   holderName  — string
//   expiry      — "MM/YY"
//   cvv         — shown on the back
//   status      — "pending" | "active" | "frozen" | "rejected" | "preview"
//   revealed    — show full PAN or masked
//   flipped     — controlled flip state
//   onFlip      — called when the card is tapped (pass to enable)
//   selected    — highlights the card (used in the picker)
//   compact     — smaller size (used in the picker list)
// ===============================================================

import React from "react";

const TIER_STYLES = {
  classic: {
    front: "linear-gradient(135deg, #0a1628 0%, #122a4d 45%, #0a1628 100%)",
    back: "linear-gradient(135deg, #0a1628 0%, #0d1f3c 45%, #0a1628 100%)",
    brand: "#e2e8f0",
    accent: "#94a3b8",
    numberColor: "#f1f5f9",
    label: "CLASSIC",
    glow1: "rgba(148,163,184,0.25)",
    glow2: "rgba(56,189,248,0.15)",
  },
  gold: {
    front: "linear-gradient(135deg, #0c0a09 0%, #29241a 40%, #1c1917 70%, #0c0a09 100%)",
    back: "linear-gradient(135deg, #0c0a09 0%, #1c1917 45%, #0c0a09 100%)",
    brand: "#f5d97e",
    accent: "#d4af37",
    numberColor: "#fef3c7",
    label: "GOLD",
    glow1: "rgba(212,175,55,0.35)",
    glow2: "rgba(245,217,126,0.12)",
  },
  platinum: {
    front: "linear-gradient(135deg, #e5e7eb 0%, #f8fafc 30%, #cbd5e1 65%, #e2e8f0 100%)",
    back: "linear-gradient(135deg, #cbd5e1 0%, #94a3b8 45%, #cbd5e1 100%)",
    brand: "#0f172a",
    accent: "#475569",
    numberColor: "#1e293b",
    label: "PLATINUM",
    glow1: "rgba(255,255,255,0.6)",
    glow2: "rgba(148,163,184,0.3)",
  },
};

const ChipSVG = ({ dark }) => (
  <svg width="46" height="34" viewBox="0 0 46 34" fill="none">
    <rect x="1" y="1" width="44" height="32" rx="6" fill="url(#chipGold)" stroke={dark ? "#8b6914" : "#b8860b"} strokeWidth="1" />
    <path d="M1 12h14M1 22h14M31 12h14M31 22h14M23 1v10M23 23v10M15 12v10M31 12v10" stroke="#8b6914" strokeWidth="1.4" />
    <defs>
      <linearGradient id="chipGold" x1="0" y1="0" x2="46" y2="34">
        <stop stopColor="#f5d97e" />
        <stop offset="0.5" stopColor="#d4af37" />
        <stop offset="1" stopColor="#b8860b" />
      </linearGradient>
    </defs>
  </svg>
);

const ContactlessSVG = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M6 9a6 6 0 0 1 0 6" opacity="0.4" />
    <path d="M9.5 6.5a10 10 0 0 1 0 11" opacity="0.6" />
    <path d="M13 4a14.5 14.5 0 0 1 0 16" opacity="0.8" />
    <path d="M16.5 1.5a19 19 0 0 1 0 21" />
  </svg>
);

function maskNumber(num) {
  if (!num) return "5399 •••• •••• ••••";
  const digits = num.replace(/\s/g, "");
  if (digits.length < 8) return num;
  return `${digits.slice(0, 4)} •••• •••• ${digits.slice(-4)}`;
}

export default function VirtualCardDisplay({
  tier = "classic",
  cardNumber = "5399 •••• •••• ••••",
  holderName = "PROBANK USER",
  expiry = "••/••",
  cvv = "•••",
  status = "active",
  revealed = false,
  flipped = false,
  onFlip,
  selected = false,
  compact = false,
}) {
  const style = TIER_STYLES[tier] || TIER_STYLES.classic;
  const isPlatinum = tier === "platinum";
  const textColor = isPlatinum ? "#1e293b" : "#ffffff";
  const subColor = isPlatinum ? "#64748b" : "#94a3b8";
  const displayNumber = revealed ? cardNumber : maskNumber(cardNumber);
  const clickable = typeof onFlip === "function";

  return (
    <div
      className={`w-full mx-auto select-none ${clickable ? "cursor-pointer" : ""} ${
        compact ? "max-w-xs" : "max-w-md"
      }`}
      style={{ perspective: "1200px" }}
      onClick={clickable ? onFlip : undefined}
    >
      <div
        className="relative w-full transition-transform duration-700"
        style={{
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          aspectRatio: "1.586",
        }}
      >
        {/* ---------------- FRONT ---------------- */}
        <div
          className={`absolute inset-0 rounded-2xl overflow-hidden transition-shadow duration-300 ${
            selected ? "ring-4 ring-amber-400/80" : ""
          }`}
          style={{
            backfaceVisibility: "hidden",
            background: style.front,
            boxShadow: selected
              ? "0 25px 60px -12px rgba(245,158,11,0.45), 0 18px 36px -18px rgba(0,0,0,0.7)"
              : "0 25px 50px -12px rgba(0,0,0,0.65)",
          }}
        >
          {/* light sweep / sheen */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.09) 45%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.09) 55%, transparent 70%)",
            }}
          />
          {/* decorative glows */}
          <div
            className="absolute -right-16 -top-24 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${style.glow1} 0%, transparent 65%)` }}
          />
          <div
            className="absolute -left-20 -bottom-28 w-80 h-80 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${style.glow2} 0%, transparent 65%)` }}
          />
          {/* fine grid texture */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              opacity: isPlatinum ? 0.05 : 0.06,
              backgroundImage:
                "linear-gradient(rgba(127,127,127,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(127,127,127,0.6) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />

          <div className="relative h-full flex flex-col justify-between p-5 sm:p-6">
            {/* top row */}
            <div className="flex items-start justify-between">
              <div>
                <div
                  className={`font-extrabold tracking-widest ${compact ? "text-sm" : "text-lg sm:text-xl"}`}
                  style={{ color: style.brand }}
                >
                  PRO<span style={{ color: style.accent }}>BANK</span>
                </div>
                <div
                  className="tracking-[0.3em] mt-0.5"
                  style={{ color: subColor, fontSize: compact ? 8 : 10 }}
                >
                  {style.label} VIRTUAL
                </div>
              </div>
              <ContactlessSVG color={isPlatinum ? "rgba(30,41,59,0.55)" : "rgba(255,255,255,0.7)"} />
            </div>

            {/* chip */}
            <div className="mt-1" style={{ transform: compact ? "scale(0.85)" : "none", transformOrigin: "left center" }}>
              <ChipSVG dark={isPlatinum} />
            </div>

            {/* number */}
            <div
              className={`font-mono tracking-wider ${compact ? "text-xs" : "text-base sm:text-xl md:text-2xl"}`}
              style={{
                color: style.numberColor,
                textShadow: isPlatinum ? "none" : "0 2px 6px rgba(0,0,0,0.6)",
              }}
            >
              {displayNumber}
            </div>

            {/* bottom row */}
            <div className="flex items-end justify-between">
              <div className="min-w-0">
                <div style={{ color: subColor, fontSize: compact ? 7 : 9 }} className="tracking-widest">
                  CARD HOLDER
                </div>
                <div
                  className={`font-semibold tracking-wide uppercase truncate ${compact ? "text-[10px]" : "text-xs sm:text-sm"}`}
                  style={{ color: textColor }}
                >
                  {holderName}
                </div>
              </div>
              <div className="mx-3">
                <div style={{ color: subColor, fontSize: compact ? 7 : 9 }} className="tracking-widest">
                  EXPIRES
                </div>
                <div className={`font-semibold ${compact ? "text-[10px]" : "text-xs sm:text-sm"}`} style={{ color: textColor }}>
                  {expiry}
                </div>
              </div>
              <div
                className={`font-black italic tracking-tight ${compact ? "text-sm" : "text-lg sm:text-xl"}`}
                style={{ color: textColor, textShadow: isPlatinum ? "none" : "0 1px 4px rgba(0,0,0,0.5)" }}
              >
                VISA
              </div>
            </div>
          </div>

          {/* status overlays */}
          {status === "pending" && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex flex-col items-center justify-center">
              <div className="text-3xl mb-2">⏳</div>
              <div className="text-amber-300 font-bold tracking-widest text-sm">PENDING APPROVAL</div>
            </div>
          )}
          {status === "frozen" && (
            <div className="absolute inset-0 bg-sky-950/70 backdrop-blur-[2px] flex flex-col items-center justify-center">
              <div className="text-3xl mb-2">❄️</div>
              <div className="text-sky-300 font-bold tracking-widest text-sm">CARD FROZEN</div>
            </div>
          )}
          {status === "rejected" && (
            <div className="absolute inset-0 bg-red-950/70 backdrop-blur-[2px] flex items-center justify-center">
              <div className="text-red-300 font-bold tracking-widest text-sm">APPLICATION DECLINED</div>
            </div>
          )}
        </div>

        {/* ---------------- BACK ---------------- */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            background: style.back,
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.65)",
          }}
        >
          <div className="h-full flex flex-col">
            <div className="w-full h-12 bg-black mt-6" style={{ opacity: isPlatinum ? 0.85 : 1 }} />
            <div className="px-5 mt-5">
              <div className="flex items-center gap-3">
                <div
                  className="flex-1 h-9 rounded bg-slate-200/90 flex items-center px-3"
                  style={{
                    backgroundImage: "repeating-linear-gradient(45deg, #cbd5e1 0 6px, #e2e8f0 6px 12px)",
                  }}
                >
                  <span className="text-slate-500 italic text-xs font-serif">{holderName}</span>
                </div>
                <div className="h-9 px-4 rounded bg-white flex items-center">
                  <span className="text-black font-mono font-bold tracking-widest text-sm">{cvv}</span>
                </div>
              </div>
              <div
                className="mt-1.5 text-right tracking-widest"
                style={{ color: isPlatinum ? "#475569" : "#64748b", fontSize: 9 }}
              >
                CVV
              </div>
            </div>

            <div className="flex-1" />

            <div className="p-5 flex items-end justify-between">
              <div
                className="leading-relaxed max-w-[65%]"
                style={{ color: isPlatinum ? "#475569" : "#64748b", fontSize: 10 }}
              >
                This virtual card is issued by ProBank for online payments. Use only on trusted websites.
              </div>
              <div className="font-extrabold tracking-widest text-sm" style={{ color: style.brand }}>
                PRO<span style={{ color: style.accent }}>BANK</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {clickable && !compact && (
        <div className="text-center text-[11px] text-slate-500 mt-3">Tap card to flip</div>
      )}
    </div>
  );
}
